/**
 * Serwis API dla modułu Kasa/Bank
 * Operacje finansowe i zarządzanie saldami
 */

import api from './api';

export const kasaBankService = {
  // Pobierz aktualne saldo
  getSaldo: async (locationId = null) => {
    try {
      const url = locationId ? `kasa-bank/saldo?location_id=${locationId}` : 'kasa-bank/saldo';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania salda:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania salda');
    }
  },

  // Pobierz operacje finansowe (server-side filtrowanie + paginacja)
  // Wsteczna kompatybilnosc: jezeli pierwszym argumentem jest liczba, traktujemy ja jako limit (stary signature).
  getOperacje: async (paramsOrLimit = {}, legacyLocationId = null, signal = null) => {
    try {
      let limit = 50;
      let offset = 0;
      let dateFrom;
      let dateTo;
      let typPlatnosci;
      let locationId = null;
      let abortSignal = signal;

      if (typeof paramsOrLimit === 'number') {
        limit = paramsOrLimit;
        locationId = legacyLocationId;
      } else if (paramsOrLimit && typeof paramsOrLimit === 'object') {
        ({
          limit = 50,
          offset = 0,
          dateFrom,
          dateTo,
          typPlatnosci,
          locationId = null
        } = paramsOrLimit);
        if (legacyLocationId && typeof legacyLocationId === 'object' && legacyLocationId.aborted !== undefined) {
          abortSignal = legacyLocationId;
        }
      }

      const qs = new URLSearchParams();
      qs.set('limit', String(limit));
      qs.set('offset', String(offset));
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      if (typPlatnosci) qs.set('typ_platnosci', typPlatnosci);
      if (locationId) qs.set('location_id', String(locationId));

      const response = await api.get(`kasa-bank/operacje?${qs.toString()}`, abortSignal ? { signal: abortSignal } : undefined);
      return response.data;
    } catch (error) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') throw error;
      console.error('Błąd pobierania operacji:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania operacji');
    }
  },

  // Pobierz statystyki dzienne
  getDailySummary: async (date = null, locationId = null) => {
    try {
      let url = date ? `kasa-bank/summary/daily?date=${date}` : 'kasa-bank/summary/daily';
      if (locationId) {
        url += (url.includes('?') ? '&' : '?') + `location_id=${locationId}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania podsumowania dziennego:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania podsumowania dziennego');
    }
  },

  // Pobierz statystyki miesięczne
  getMonthlyStats: async (month = null, year = null, locationId = null) => {
    try {
      let url = 'kasa-bank/stats/monthly';
      const params = [];
      if (month && year) {
        params.push(`month=${month}&year=${year}`);
      }
      if (locationId) {
        params.push(`location_id=${locationId}`);
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania statystyk miesięcznych:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania statystyk miesięcznych');
    }
  },

  // Dodaj nową operację finansową
  addOperation: async (operationData) => {
    try {
      const response = await api.post('kasa-bank/operacje', operationData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Błąd dodawania operacji');
    }
  },

  // Pobierz płatności pogrupowane według typu
  getPaymentsByType: async (dateFrom = null, dateTo = null, locationId = null) => {
    try {
      let url = 'kasa-bank/payments/by-type';
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (locationId) params.push(`location_id=${locationId}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania płatności według typu:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania płatności według typu');
    }
  },

  // Pobierz dokumenty KP (Kasa Przyjmie)
  // Pobierz dokumenty KP - wsteczna kompatybilnosc:
  //  - stara sygnatura: getKPDocuments(limit, offset, dateFrom, dateTo, locationId, signal)
  //  - nowa sygnatura:  getKPDocuments({limit, offset, dateFrom, dateTo, locationId}, signal)
  getKPDocuments: async (...args) => _getDocuments('kp', ...args),

  // Pobierz dokumenty KW
  getKWDocuments: async (...args) => _getDocuments('kw', ...args),

  // Utwórz nową operację KP/KW
  createOperacja: async (operacjaData) => {
    try {
      const response = await api.post('kasa-bank/operacje', operacjaData);
      return response.data;
    } catch (error) {
      console.error('Błąd tworzenia operacji:', error);
      throw new Error(error.response?.data?.message || 'Błąd tworzenia operacji');
    }
  },

  // Aktualizuj operację
  updateOperacja: async (operacjaId, operacjaData) => {
    try {
      const response = await api.put(`kasa-bank/operacje/${operacjaId}`, operacjaData);
      return response.data;
    } catch (error) {
      console.error('Błąd aktualizacji operacji:', error);
      throw new Error(error.response?.data?.message || 'Błąd aktualizacji operacji');
    }
  },

  // Pobierz kategorie operacji
  getKategorie: async () => {
    try {
      const response = await api.get('kasa-bank/kategorie');
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania kategorii:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania kategorii');
    }
  },
};

// Helper dla getKPDocuments / getKWDocuments wspierajacy obie sygnatury.
async function _getDocuments(typ /* 'kp' | 'kw' */, paramsOrLimit = {}, ...rest) {
  let limit = 50;
  let offset = 0;
  let dateFrom;
  let dateTo;
  let locationId = null;
  let abortSignal;

  if (typeof paramsOrLimit === 'number') {
    // stara sygnatura: (limit, offset, dateFrom, dateTo, locationId, signal)
    limit = paramsOrLimit;
    offset = rest[0] ?? 0;
    dateFrom = rest[1] ?? undefined;
    dateTo = rest[2] ?? undefined;
    locationId = rest[3] ?? null;
    abortSignal = rest[4];
  } else if (paramsOrLimit && typeof paramsOrLimit === 'object') {
    ({
      limit = 50,
      offset = 0,
      dateFrom,
      dateTo,
      locationId = null
    } = paramsOrLimit);
    if (rest[0] && typeof rest[0] === 'object' && rest[0].aborted !== undefined) {
      abortSignal = rest[0];
    }
  }

  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  qs.set('offset', String(offset));
  if (dateFrom) qs.set('date_from', dateFrom);
  if (dateTo) qs.set('date_to', dateTo);
  if (locationId) qs.set('location_id', String(locationId));

  try {
    const response = await api.get(`kasa-bank/documents/${typ}?${qs.toString()}`, abortSignal ? { signal: abortSignal } : undefined);
    return response.data;
  } catch (error) {
    if (error?.name === 'AbortError' || error?.name === 'CanceledError') throw error;
    console.error(`Błąd pobierania dokumentów ${typ.toUpperCase()}:`, error);
    throw new Error(error.response?.data?.message || `Błąd pobierania dokumentów ${typ.toUpperCase()}`);
  }
}

export default kasaBankService;
